import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { FaCheckCircle, FaClipboardList, FaClock } from 'react-icons/fa';
import {
  buildInventoryOwnerPayload,
  createInventoryPurchase,
  fetchGroceryProducts,
  fetchInventoryPurchaseById,
  fetchInventoryPurchasesByOwner,
  fetchSuppliersByOwner,
  getInventoryOwnerContext,
  getLocalDateString,
  isGroceryOrderPurchase,
  PURCHASE_PAYMENT_TYPE_OPTIONS,
  purchaseStoreDisplay,
  purchaseSupplierDisplay,
  updateInventoryPurchase,
} from './inventoryApi';
import { getInventoryOwnerRequiredMessage } from './inventoryEntity';
import SearchableSelect from './SearchableSelect';
import OrdersTablePagination from './OrdersTablePagination';
import OrdersSortableTh from './OrdersSortableTh';
import { buildSortHandlers, parseDateSortValue, sortList, str } from './tableSortUtils';
import { AdminNameCell, AdminRowActions, rowClickHandler } from '../admin/AdminRowCells';
import './css/Inventory.css';
import './css/Menu.css';
import './css/OrdersTablePagination.css';
import './css/Receivables.css';

const PURCHASE_STATUS_OPTIONS = ['NEW', 'RECEIVED'];

function normalizePurchaseStatusForUi(status) {
  const s = String(status ?? '').trim();
  if (!s) return 'NEW';
  if (s.toLowerCase() === 'received') return 'RECEIVED';
  if (s.toUpperCase() === 'RECEIVED') return 'RECEIVED';
  if (s.toUpperCase() === 'NEW') return 'NEW';
  return s.toUpperCase();
}

function isPurchaseEditable(status) {
  return normalizePurchaseStatusForUi(status) !== 'RECEIVED';
}

function toDatetimeLocalValue(val) {
  if (val == null || String(val).trim() === '') return '';
  try {
    const normalized = String(val).trim().replace(' ', 'T');
    const d = new Date(normalized);
    if (Number.isNaN(d.getTime())) return '';
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const h = String(d.getHours()).padStart(2, '0');
    const min = String(d.getMinutes()).padStart(2, '0');
    return `${y}-${m}-${day}T${h}:${min}`;
  } catch {
    return '';
  }
}

/** API expects `YYYY-MM-DD HH:mm:ss`. */
function toApiDateTime(val) {
  if (val == null || String(val).trim() === '') return '';
  const s = String(val).trim().replace('T', ' ');
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/.test(s)) return s;
  if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(s)) return `${s}:00`;
  return s;
}

function resolveExpDeliveryDateFromPurchase(row, fallback = '') {
  const v =
    row?.expDeliveryDate ??
    row?.Exp_Delivery_Date ??
    row?.exp_delivery_date ??
    fallback;
  return v != null && String(v).trim() !== '' ? String(v).trim() : '';
}

/** Grocery-order purchases carry a store name; supplier purchases carry a supplier name. */
function purchaseCounterpartyName(row) {
  const name = isGroceryOrderPurchase(row) ? purchaseStoreDisplay(row) : purchaseSupplierDisplay(row);
  if (name && name !== '—') return name;
  const pid = row?.purchaseId ?? row?.Purchase_Id;
  return pid != null ? `Purchase ${pid}` : '—';
}

function getPurchaseSortValue(row, sortKey) {
  switch (sortKey) {
    case 'purchaseId':
      return Number(row.purchaseId ?? row.Purchase_Id) || 0;
    case 'name':
      return str(purchaseCounterpartyName(row));
    case 'source':
      return isGroceryOrderPurchase(row) ? 1 : 0;
    case 'items':
      return Array.isArray(row.lineItems ?? row.items) ? (row.lineItems ?? row.items).length : 0;
    case 'totalAmount':
      return Number.parseFloat(row.totalAmount ?? row.Total_Amount) || 0;
    case 'status':
      return normalizePurchaseStatusForUi(row.purchaseStatus ?? row.Purchase_Status);
    case 'expDeliveryDate':
      return parseDateSortValue(resolveExpDeliveryDateFromPurchase(row));
    case 'purchaseDate':
      return parseDateSortValue(row.purchaseDate ?? row.Purchase_Date);
    default:
      return '';
  }
}

const emptyLineItem = () => ({
  productId: '',
  quantity: '',
  pricePerUnit: '',
  discountPercent: '',
});

const initialForm = () => ({
  supplierId: '',
  supplierName: '',
  billNumber: '',
  paymentType: '',
  purchaseDate: getLocalDateString(),
  items: [],
  draftLine: emptyLineItem(),
});

function formatLineAmount(line) {
  const qty = Number(line.quantity);
  const price = Number(line.pricePerUnit);
  if (!Number.isFinite(qty) || !Number.isFinite(price)) return '—';
  const discountRaw = line.discountPercent;
  const discount =
    discountRaw !== '' && discountRaw != null && String(discountRaw).trim() !== ''
      ? Number(discountRaw)
      : 0;
  const factor = Number.isFinite(discount) && discount > 0 ? 1 - discount / 100 : 1;
  return `₹${(qty * price * factor).toFixed(2)}`;
}

function validateLineItem(line, lineLabel) {
  const quantity = Number(line.quantity);
  const pricePerUnit = Number(line.pricePerUnit);
  const lineId = Number(line.productId);

  if (!lineId || Number.isNaN(lineId)) {
    throw new Error(`${lineLabel}: select a product.`);
  }

  if (!quantity || quantity <= 0 || Number.isNaN(quantity)) {
    throw new Error(`${lineLabel}: enter a valid quantity.`);
  }
  if (Number.isNaN(pricePerUnit) || pricePerUnit < 0) {
    throw new Error(`${lineLabel}: enter a valid price per unit.`);
  }

  const entry = { productId: lineId, quantity, pricePerUnit };
  const discount = line.discountPercent;
  if (discount !== '' && discount != null && String(discount).trim() !== '') {
    const discountPercent = Number(discount);
    if (Number.isNaN(discountPercent) || discountPercent < 0) {
      throw new Error(`${lineLabel}: invalid discount percent.`);
    }
    entry.discountPercent = discountPercent;
  }
  return entry;
}

function formatPurchaseDate(val) {
  if (!val) return '—';
  const s = String(val).trim();
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s.slice(0, 10);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} ${hours}:${mins}`;
  } catch {
    return s.slice(0, 16);
  }
}

function formatPurchaseStatus(status) {
  if (status == null || String(status).trim() === '') return '—';
  return normalizePurchaseStatusForUi(status);
}

function formatPurchaseAmount(val) {
  if (val == null || val === '') return '—';
  const amount = Number.parseFloat(val);
  if (!Number.isFinite(amount)) return '—';
  return `₹${amount.toFixed(2)}`;
}

function PurchaseStatusBadge({ status }) {
  const normalized = normalizePurchaseStatusForUi(status);
  const tone = normalized === 'RECEIVED' ? 'received' : 'new';
  return (
    <span className={`purchase-status-badge purchase-status-badge--${tone}`}>
      {formatPurchaseStatus(status)}
    </span>
  );
}

const InventoryPurchases = ({ preselectedSupplierId, preselectedSupplierName, onPreselectedSupplierConsumed }) => {
  const navigate = useNavigate();
  const owner = getInventoryOwnerContext();
  const { ownerId, isStore } = owner;
  const user = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem('user') || 'null');
    } catch {
      return null;
    }
  }, []);
  const createdById = Number(user?.userId || user?.id || user?.User_Id || 0);
  const updatedById = createdById;

  const [purchases, setPurchases] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [groceryProducts, setGroceryProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listMessage, setListMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [purchasesPage, setPurchasesPage] = useState(1);
  const [purchasesPageSize, setPurchasesPageSize] = useState(10);
  const [purchasesSort, setPurchasesSort] = useState(null);
  const [purchaseListSearch, setPurchaseListSearch] = useState('');
  const [purchaseStatFilter, setPurchaseStatFilter] = useState('all');
  const [itemsPopupPurchaseId, setItemsPopupPurchaseId] = useState(null);
  const [itemsPopupSummary, setItemsPopupSummary] = useState(null);
  const [purchaseDetailsById, setPurchaseDetailsById] = useState({});
  const [detailLoadingId, setDetailLoadingId] = useState(null);
  const [detailError, setDetailError] = useState('');
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [updatePurchaseId, setUpdatePurchaseId] = useState(null);
  const [updateSummary, setUpdateSummary] = useState(null);
  const [updateForm, setUpdateForm] = useState({ purchaseStatus: 'NEW', expDeliveryDate: '' });
  const [updateError, setUpdateError] = useState('');
  const [updateSubmitting, setUpdateSubmitting] = useState(false);
  const [updateLoading, setUpdateLoading] = useState(false);
  const loadPurchases = useCallback(async () => {
    const ctx = getInventoryOwnerContext();
    if (!ctx.ownerId) {
      setPurchases([]);
      return;
    }
    try {
      setLoading(true);
      const rows = await fetchInventoryPurchasesByOwner(ctx);
      setPurchases(Array.isArray(rows) ? rows : []);
      setListMessage('');
    } catch (err) {
      console.error(
        'Failed to load purchases:',
        err?.response?.status,
        err?.response?.data ?? err?.message
      );
      setPurchases([]);
      setListMessage(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Could not load purchases.'
      );
    } finally {
      setLoading(false);
    }
  }, [ownerId]);

  const loadLookups = useCallback(async () => {
    const ctx = getInventoryOwnerContext();
    try {
      const [supplierRows, products] = await Promise.all([
        ctx.ownerId ? fetchSuppliersByOwner(ctx) : Promise.resolve([]),
        ctx.ownerId ? fetchGroceryProducts() : Promise.resolve([]),
      ]);
      setSuppliers(Array.isArray(supplierRows) ? supplierRows : []);
      setGroceryProducts(Array.isArray(products) ? products : []);
    } catch (err) {
      console.error('Failed to load purchase lookups:', err?.response?.data ?? err?.message);
      setSuppliers([]);
      setGroceryProducts([]);
    }
  }, [ownerId, isStore]);

  useEffect(() => {
    loadPurchases();
    loadLookups();
  }, [loadPurchases, loadLookups]);

  useEffect(() => {
    setPurchasesPage(1);
    setPurchaseListSearch('');
    setPurchaseStatFilter('all');
  }, [ownerId]);

  const purchaseListSearchQuery = purchaseListSearch.trim();

  const togglePurchaseStatFilter = (next) => {
    setPurchaseStatFilter((prev) => (prev === next ? 'all' : next));
    setPurchasesPage(1);
  };

  const filteredPurchases = useMemo(() => {
    const q = purchaseListSearchQuery.toLowerCase();
    return purchases.filter((row) => {
      const status = normalizePurchaseStatusForUi(row?.purchaseStatus ?? row?.Purchase_Status);
      if (purchaseStatFilter === 'new' && status === 'RECEIVED') return false;
      if (purchaseStatFilter === 'received' && status !== 'RECEIVED') return false;

      if (!q) return true;
      const statusLabel = formatPurchaseStatus(row?.purchaseStatus);
      const itemNames = (Array.isArray(row?.items) ? row.items : [])
        .map((line) => {
          const name = String(line?.productName ?? line?.Product_Name ?? '').trim();
          if (name) return name;
          const pid = line?.productId ?? line?.Product_Id;
          if (pid == null) return '';
          const prod = groceryProducts.find(
            (p) => String(p.groceryProductId ?? p.id) === String(pid)
          );
          return prod?.productName ? String(prod.productName).trim() : '';
        })
        .filter(Boolean);
      const fields = [
        row?.purchaseId,
        row?.Purchase_Id,
        purchaseSupplierDisplay(row),
        purchaseStoreDisplay(row),
        row?.supplierName,
        row?.Supplier_Name,
        row?.storeName,
        row?.Store_Name,
        row?.groceryOrderId,
        row?.purchaseStatus,
        statusLabel,
        row?.totalAmount,
        row?.purchaseDate,
        resolveExpDeliveryDateFromPurchase(row),
        ...itemNames,
      ];
      return fields.some((v) => v != null && String(v).toLowerCase().includes(q));
    });
  }, [purchases, purchaseListSearchQuery, groceryProducts, purchaseStatFilter]);

  useEffect(() => {
    setPurchasesPage(1);
  }, [purchaseListSearchQuery, purchaseStatFilter]);

  const sortedPurchases = useMemo(
    () => sortList(filteredPurchases, purchasesSort, getPurchaseSortValue),
    [filteredPurchases, purchasesSort]
  );

  const purchaseStats = useMemo(() => {
    let newCount = 0;
    let receivedCount = 0;
    purchases.forEach((row) => {
      const status = normalizePurchaseStatusForUi(row?.purchaseStatus ?? row?.Purchase_Status);
      if (status === 'RECEIVED') receivedCount += 1;
      else newCount += 1;
    });
    return {
      total: purchases.length,
      newCount,
      receivedCount,
    };
  }, [purchases]);

  const { handleSortAsc: handlePurchasesSortAsc, handleSortDesc: handlePurchasesSortDesc } =
    buildSortHandlers(setPurchasesSort, () => setPurchasesPage(1));

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(sortedPurchases.length / purchasesPageSize));
    setPurchasesPage((p) => Math.min(p, tp));
  }, [sortedPurchases.length, purchasesPageSize]);

  useEffect(() => {
    if (isStore) {
      const name = preselectedSupplierName != null ? String(preselectedSupplierName).trim() : '';
      if (!name) return;
      setForm({ ...initialForm(), supplierName: name });
      setShowModal(true);
      if (typeof onPreselectedSupplierConsumed === 'function') {
        onPreselectedSupplierConsumed();
      }
      return;
    }
    const id = preselectedSupplierId != null ? String(preselectedSupplierId).trim() : '';
    if (!id) return;
    setForm({ ...initialForm(), supplierId: id });
    setShowModal(true);
    if (typeof onPreselectedSupplierConsumed === 'function') {
      onPreselectedSupplierConsumed();
    }
  }, [preselectedSupplierId, preselectedSupplierName, onPreselectedSupplierConsumed, isStore]);

  const purchasesTotalPages = Math.max(
    1,
    Math.ceil(sortedPurchases.length / purchasesPageSize)
  );
  const purchasesPageSafe = Math.min(Math.max(1, purchasesPage), purchasesTotalPages);
  const purchasesPageStart = (purchasesPageSafe - 1) * purchasesPageSize;
  const pagedPurchases = sortedPurchases.slice(
    purchasesPageStart,
    purchasesPageStart + purchasesPageSize
  );

  const handlePurchasesPageChange = (nextPage) => {
    setPurchasesPage(nextPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePurchasesPageSizeChange = (nextSize) => {
    setPurchasesPageSize(nextSize);
    setPurchasesPage(1);
  };

  const supplierSelectOptions = useMemo(
    () =>
      suppliers
        .map((s) => {
          const name = s.supplierName ?? s.Supplier_Name ?? '';
          const id = s.supplierId ?? s.Supplier_Id;
          if (!name) return null;
          return {
            value: isStore ? name : String(id),
            label: name,
          };
        })
        .filter(Boolean),
    [suppliers, isStore]
  );

  const suppliersById = useMemo(() => {
    const map = new Map();
    suppliers.forEach((s) => {
      const id = s.supplierId ?? s.Supplier_Id;
      if (id != null) map.set(String(id), s);
    });
    return map;
  }, [suppliers]);

  const groceryProductSelectOptions = useMemo(
    () =>
      groceryProducts.map((prod) => {
        const id = prod.groceryProductId ?? prod.id;
        return {
          value: String(id),
          label: String(prod.productName ?? '').trim() || `Product ${id}`,
        };
      }),
    [groceryProducts]
  );

  const findGroceryProduct = (productId) =>
    groceryProducts.find((p) => String(p.groceryProductId ?? p.id) === String(productId));

  const productUnitSymbol = (productId) => {
    const row = findGroceryProduct(productId);
    if (!row) return '';
    return String(row.unitSymbol ?? row.Unit_Symbol ?? row.unitName ?? row.Unit_Name ?? '').trim();
  };

  const productLabel = (productId) => {
    const row = findGroceryProduct(productId);
    if (!row) return `Product ${productId}`;
    return String(row.productName ?? '').trim() || `Product ${productId}`;
  };

  const productNameOnly = (productId) => {
    const row = findGroceryProduct(productId);
    if (row?.productName) return String(row.productName).trim();
    return `Product ${productId}`;
  };

  const getPurchaseDetail = useCallback(
    (purchaseId) => {
      const key = String(purchaseId);
      const cached = purchaseDetailsById[key];
      if (cached && Array.isArray(cached.items)) return cached;
      const row = purchases.find((p) => String(p.purchaseId ?? p.Purchase_Id) === key);
      if (row && Array.isArray(row.items) && row.items.length > 0) return row;
      return cached ?? null;
    },
    [purchaseDetailsById, purchases]
  );

  const handleOpen = () => {
    setSubmitError('');
    setForm(initialForm());
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
    setSubmitError('');
    setForm(initialForm());
  };

  const handleDraftChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      draftLine: { ...prev.draftLine, [field]: value },
    }));
  };

  const handleDraftProductChange = (productId) => {
    setForm((prev) => ({
      ...prev,
      draftLine: { ...prev.draftLine, productId },
    }));
  };

  const commitDraftLine = () => {
    setSubmitError('');
    try {
      validateLineItem(form.draftLine, 'Current line');
      setForm((prev) => ({
        ...prev,
        items: [...prev.items, { ...prev.draftLine }],
        draftLine: emptyLineItem(),
      }));
    } catch (err) {
      setSubmitError(err.message || 'Invalid line item.');
    }
  };

  const removeLineItem = (index) => {
    setForm((prev) => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index),
    }));
  };

  const buildItemsPayload = () => {
    const items = [];
    for (let i = 0; i < form.items.length; i += 1) {
      items.push(validateLineItem(form.items[i], `Line ${i + 1}`));
    }
    if (items.length === 0) {
      throw new Error('Add at least one product line.');
    }
    return items;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');

    if (!ownerId) {
      setSubmitError(getInventoryOwnerRequiredMessage());
      return;
    }
    if (!createdById) {
      setSubmitError('Please sign in again.');
      return;
    }
    if (!String(form.purchaseDate || '').trim()) {
      setSubmitError('Enter the purchase date.');
      return;
    }
    if (isStore) {
      const supplierName = String(form.supplierName || '').trim();
      if (!supplierName) {
        setSubmitError('Select a supplier.');
        return;
      }
    } else {
      const supplierId = Number(form.supplierId);
      if (!form.supplierId || Number.isNaN(supplierId) || supplierId <= 0) {
        setSubmitError('Select a supplier.');
        return;
      }
    }

    let items;
    try {
      const draftHasData =
        form.draftLine.productId ||
        form.draftLine.quantity ||
        form.draftLine.pricePerUnit ||
        form.draftLine.discountPercent;
      if (draftHasData) {
        validateLineItem(form.draftLine, 'Current line');
        items = [
          ...form.items.map((line, i) => validateLineItem(line, `Line ${i + 1}`)),
          validateLineItem(form.draftLine, 'Current line', isStore),
        ];
      } else {
        items = buildItemsPayload();
      }
    } catch (err) {
      setSubmitError(err.message || 'Invalid line items.');
      return;
    }

    try {
      setIsSubmitting(true);
      const payload = {
        ...buildInventoryOwnerPayload(owner),
        createdById,
        items,
        purchaseDate: `${String(form.purchaseDate).trim()}T12:00:00`,
      };
      if (isStore) {
        payload.supplierName = String(form.supplierName || '').trim();
      } else {
        payload.supplierId = Number(form.supplierId);
        const billNumber = String(form.billNumber || '').trim();
        const paymentType = String(form.paymentType || '').trim();
        if (billNumber) payload.billNumber = billNumber;
        if (paymentType) payload.paymentType = paymentType;
      }
      const data = await createInventoryPurchase(payload, owner);
      const created = data?.purchase;
      if (created?.purchaseId != null) {
        setPurchaseDetailsById((prev) => ({
          ...prev,
          [String(created.purchaseId)]: created,
        }));
      }
      await loadPurchases();
      handleClose();
      alert(data?.message || 'Inventory purchase created');
    } catch (err) {
      setSubmitError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Failed to create inventory purchase.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeItemsPopup = () => {
    setItemsPopupPurchaseId(null);
    setItemsPopupSummary(null);
    setDetailError('');
  };

  const openItemsPopup = async (purchaseId, summaryRow) => {
    if (purchaseId == null) return;
    const key = String(purchaseId);
    setItemsPopupPurchaseId(key);
    setItemsPopupSummary(summaryRow ?? null);
    setDetailError('');

    const existing = getPurchaseDetail(purchaseId);
    if (existing?.items?.length) return;

    setDetailLoadingId(key);
    try {
      const detail = await fetchInventoryPurchaseById(purchaseId, owner);
      setPurchaseDetailsById((prev) => ({ ...prev, [key]: detail }));
    } catch (err) {
      console.error(
        'Failed to load purchase detail:',
        err?.response?.status,
        err?.response?.data ?? err?.message
      );
      setDetailError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Could not load purchase items.'
      );
    } finally {
      setDetailLoadingId(null);
    }
  };

  const popupDetail =
    itemsPopupPurchaseId != null ? getPurchaseDetail(itemsPopupPurchaseId) : null;
  const popupLineItems = Array.isArray(popupDetail?.items) ? popupDetail.items : [];
  const popupLoading =
    itemsPopupPurchaseId != null && detailLoadingId === itemsPopupPurchaseId;
  const popupHeader = popupDetail ?? itemsPopupSummary ?? {};
  const popupTotalAmount = (() => {
    const headerTotal = popupHeader?.totalAmount ?? popupHeader?.Total_Amount;
    if (headerTotal != null && headerTotal !== '') return headerTotal;
    if (popupLineItems.length === 0) return null;
    const sum = popupLineItems.reduce((acc, line) => {
      const amt = line.purchaseAmount ?? line.totalPrice;
      const n = Number(amt);
      return Number.isFinite(n) ? acc + n : acc;
    }, 0);
    return sum > 0 || popupLineItems.some((l) => l.purchaseAmount != null || l.totalPrice != null)
      ? sum
      : null;
  })();

  const closeUpdateModal = () => {
    setShowUpdateModal(false);
    setUpdatePurchaseId(null);
    setUpdateSummary(null);
    setUpdateError('');
    setUpdateForm({ purchaseStatus: 'NEW', expDeliveryDate: '' });
  };

  const mergePurchaseIntoState = (updated) => {
    if (!updated?.purchaseId) return;
    const key = String(updated.purchaseId);
    setPurchaseDetailsById((prev) => {
      const existing = prev[key];
      return {
        ...prev,
        [key]: {
          ...existing,
          ...updated,
          items: updated.items ?? existing?.items,
        },
      };
    });
    setPurchases((prev) =>
      prev.map((p) => {
        if (String(p.purchaseId ?? p.Purchase_Id) !== key) return p;
        return { ...p, ...updated, items: p.items };
      })
    );
    if (itemsPopupPurchaseId === key) {
      setItemsPopupSummary((prev) => (prev ? { ...prev, ...updated } : prev));
    }
  };

  const openUpdateModal = async (purchaseId, summaryRow) => {
    if (purchaseId == null) return;
    const key = String(purchaseId);
    setUpdatePurchaseId(key);
    setUpdateSummary(summaryRow ?? null);
    setUpdateError('');
    setShowUpdateModal(true);

    const applyForm = (row) => {
      setUpdateForm({
        purchaseStatus: normalizePurchaseStatusForUi(row?.purchaseStatus),
        expDeliveryDate: toDatetimeLocalValue(resolveExpDeliveryDateFromPurchase(row)),
      });
    };

    applyForm(summaryRow ?? getPurchaseDetail(purchaseId) ?? {});

    setUpdateLoading(true);
    try {
      const fetched = await fetchInventoryPurchaseById(purchaseId, owner);
      setPurchaseDetailsById((prev) => ({
        ...prev,
        [key]: {
          ...prev[key],
          ...fetched,
          items: fetched.items ?? prev[key]?.items,
        },
      }));
      applyForm(fetched);
    } catch (err) {
      console.error('Failed to load purchase for update:', err?.response?.data ?? err);
      setUpdateError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Could not load latest purchase details.'
      );
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleUpdateSubmit = async (e) => {
    e.preventDefault();
    setUpdateError('');
    if (!updatePurchaseId) return;
    if (!updatedById) {
      setUpdateError('Please sign in again.');
      return;
    }
      const purchaseStatus = normalizePurchaseStatusForUi(updateForm.purchaseStatus);
      if (!PURCHASE_STATUS_OPTIONS.includes(purchaseStatus)) {
        setUpdateError('Purchase status is required.');
        return;
      }

    try {
      setUpdateSubmitting(true);
      const expDeliveryDate = toApiDateTime(updateForm.expDeliveryDate);
      const payload = {
        purchaseStatus,
        updatedById,
      };
      if (expDeliveryDate) {
        payload.expDeliveryDate = expDeliveryDate;
      }

      const data = await updateInventoryPurchase(updatePurchaseId, payload, owner);
      const key = String(updatePurchaseId);
      const fromApi = data?.purchase ?? {};

      await loadPurchases();

      const expFromServer = resolveExpDeliveryDateFromPurchase(fromApi, expDeliveryDate);
      let patch = {
        ...fromApi,
        purchaseId: fromApi.purchaseId ?? Number(updatePurchaseId),
        purchaseStatus: normalizePurchaseStatusForUi(
          fromApi.purchaseStatus ?? purchaseStatus
        ),
        expDeliveryDate: expFromServer,
      };

      try {
        const fresh = await fetchInventoryPurchaseById(updatePurchaseId, owner);
        patch = {
          ...patch,
          ...fresh,
          items: fresh.items,
          expDeliveryDate: resolveExpDeliveryDateFromPurchase(fresh, expFromServer),
        };
      } catch (refetchErr) {
        console.warn('Purchase refetch after update:', refetchErr?.response?.data ?? refetchErr);
      }

      mergePurchaseIntoState(patch);
      closeUpdateModal();
      alert(data?.message || 'Inventory purchase updated');
    } catch (err) {
      setUpdateError(
        err?.response?.data?.message ||
          err?.response?.data?.error ||
          'Failed to update inventory purchase.'
      );
    } finally {
      setUpdateSubmitting(false);
    }
  };

  if (!ownerId) {
    return (
      <div className="inventory-supplier-panel">
        <p className="inventory-status">{getInventoryOwnerRequiredMessage()}</p>
      </div>
    );
  }

  const colCount = 8;

  return (
    <div className="inventory-supplier-panel">
      {listMessage ? <p className="inventory-status">{listMessage}</p> : null}

      <div className="purchase-stats-grid purchase-stats-grid--purchases sales-stats-grid">
        <button
          type="button"
          className={`purchase-stat-card sales-stat-card sales-stat-card--primary${purchaseStatFilter === 'all' ? ' purchase-stat-card--active' : ''}`}
          onClick={() => togglePurchaseStatFilter('all')}
          aria-pressed={purchaseStatFilter === 'all'}
        >
          <div className="sales-stat-card__top">
            <span className="sales-stat-icon-wrap" aria-hidden="true">
              <FaClipboardList className="sales-stat-icon" />
            </span>
            <span className="sales-stat-label">Total purchases</span>
          </div>
          <span className="sales-stat-value purchase-stat-value--primary">{purchaseStats.total}</span>
        </button>
        <button
          type="button"
          className={`purchase-stat-card sales-stat-card sales-stat-card--warning${purchaseStatFilter === 'new' ? ' purchase-stat-card--active' : ''}`}
          onClick={() => togglePurchaseStatFilter('new')}
          aria-pressed={purchaseStatFilter === 'new'}
        >
          <div className="sales-stat-card__top">
            <span className="sales-stat-icon-wrap" aria-hidden="true">
              <FaClock className="sales-stat-icon" />
            </span>
            <span className="sales-stat-label">Pending (new)</span>
          </div>
          <span className="sales-stat-value purchase-stat-value--warning">{purchaseStats.newCount}</span>
        </button>
        <button
          type="button"
          className={`purchase-stat-card sales-stat-card sales-stat-card--success${purchaseStatFilter === 'received' ? ' purchase-stat-card--active' : ''}`}
          onClick={() => togglePurchaseStatFilter('received')}
          aria-pressed={purchaseStatFilter === 'received'}
        >
          <div className="sales-stat-card__top">
            <span className="sales-stat-icon-wrap" aria-hidden="true">
              <FaCheckCircle className="sales-stat-icon" />
            </span>
            <span className="sales-stat-label">Received</span>
          </div>
          <span className="sales-stat-value purchase-stat-value--success">{purchaseStats.receivedCount}</span>
        </button>
      </div>

      <div className="purchase-content-card">
      <div className="inventory-list-toolbar menu-toolbar">
        <div className="menu-toolbar-row inventory-purchases-toolbar-row">
          <div className="menu-food-filter-wrap">
            <input
              type="text"
              inputMode="search"
              autoComplete="off"
              className="menu-food-filter-input"
              placeholder="Search purchases…"
              value={purchaseListSearch}
              onChange={(e) => setPurchaseListSearch(e.target.value)}
              aria-label="Search purchases"
              role="searchbox"
            />
            {purchaseListSearch.trim() !== '' && (
              <button
                type="button"
                className="menu-food-filter-clear"
                aria-label="Clear search"
                onClick={() => setPurchaseListSearch('')}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            )}
          </div>
          <div className="menu-toolbar-spacer" />
          <div className="purchase-toolbar-actions">
            <button
              type="button"
              className="purchase-btn purchase-btn--secondary"
              onClick={() => navigate('/purchase/buy-grocery')}
            >
              Buy Grocery
            </button>
            <button type="button" className="purchase-btn purchase-btn--create" onClick={handleOpen}>
              <span className="purchase-add-label-desktop">Add Purchase</span>
              <span className="purchase-add-label-mobile">Add</span>
            </button>
          </div>
        </div>
      </div>

      <div className="menu-table-wrapper">
        <table className="menu-table admin-rows-table">
          <thead>
            <tr>
              <OrdersSortableTh label="Name" sortKey="name" activeSortKey={purchasesSort?.key} sortDirection={purchasesSort?.direction} onSortAsc={handlePurchasesSortAsc} onSortDesc={handlePurchasesSortDesc} />
              <OrdersSortableTh label="Source" sortKey="source" activeSortKey={purchasesSort?.key} sortDirection={purchasesSort?.direction} onSortAsc={handlePurchasesSortAsc} onSortDesc={handlePurchasesSortDesc} />
              <OrdersSortableTh label="Items" sortKey="items" activeSortKey={purchasesSort?.key} sortDirection={purchasesSort?.direction} onSortAsc={handlePurchasesSortAsc} onSortDesc={handlePurchasesSortDesc} />
              <OrdersSortableTh label="Price" sortKey="totalAmount" activeSortKey={purchasesSort?.key} sortDirection={purchasesSort?.direction} onSortAsc={handlePurchasesSortAsc} onSortDesc={handlePurchasesSortDesc} />
              <OrdersSortableTh label="Status" sortKey="status" activeSortKey={purchasesSort?.key} sortDirection={purchasesSort?.direction} onSortAsc={handlePurchasesSortAsc} onSortDesc={handlePurchasesSortDesc} />
              <OrdersSortableTh label="Expected delivery" sortKey="expDeliveryDate" activeSortKey={purchasesSort?.key} sortDirection={purchasesSort?.direction} onSortAsc={handlePurchasesSortAsc} onSortDesc={handlePurchasesSortDesc} />
              <OrdersSortableTh label="Purchased on" sortKey="purchaseDate" activeSortKey={purchasesSort?.key} sortDirection={purchasesSort?.direction} onSortAsc={handlePurchasesSortAsc} onSortDesc={handlePurchasesSortDesc} />
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} className="inventory-items-hint inventory-table-empty-cell">
                  Loading purchases…
                </td>
              </tr>
            ) : purchases.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="inventory-items-hint inventory-table-empty-cell">
                  No purchases yet.
                </td>
              </tr>
            ) : filteredPurchases.length === 0 ? (
              <tr>
                <td colSpan={colCount} className="inventory-items-hint inventory-table-empty-cell">
                  {purchaseStatFilter !== 'all' || purchaseListSearchQuery
                    ? 'No purchases match your filters.'
                    : 'No purchases match your search.'}
                </td>
              </tr>
            ) : (
              pagedPurchases.map((p) => {
                const pid = p.purchaseId ?? p.Purchase_Id;
                const key = String(pid);
                const isDetailLoading = detailLoadingId === key && itemsPopupPurchaseId === key;
                const canEdit = isPurchaseEditable(p.purchaseStatus);
                const name = purchaseCounterpartyName(p);
                const statusUi = normalizePurchaseStatusForUi(p.purchaseStatus ?? p.Purchase_Status);
                const detailPath = pid != null ? `/purchase/${encodeURIComponent(pid)}` : null;
                const supplierIdRaw = p.supplierId ?? p.Supplier_Id;
                const supplier =
                  !isGroceryOrderPurchase(p) && supplierIdRaw != null
                    ? suppliersById.get(String(supplierIdRaw))
                    : null;
                return (
                  <tr
                    key={pid}
                    className={detailPath ? 'eats-admin-row--clickable' : undefined}
                    onClick={rowClickHandler(detailPath ? () => navigate(detailPath) : null)}
                  >
                    <td>
                      <AdminNameCell
                        name={name}
                        id={pid}
                        status={statusUi}
                        tone={statusUi === 'RECEIVED' ? 'active' : 'default'}
                        to={detailPath || undefined}
                      />
                    </td>
                    <td>{isGroceryOrderPurchase(p) ? 'Grocery order' : 'Supplier'}</td>
                    <td className="inventory-purchase-items-cell">
                      {pid == null ? (
                        '—'
                      ) : isDetailLoading ? (
                        'Loading…'
                      ) : (
                        <button
                          type="button"
                          className="inventory-purchase-view-items-btn"
                          onClick={() => openItemsPopup(pid, p)}
                        >
                          View items
                        </button>
                      )}
                    </td>
                    <td className="purchase-amount-cell">{formatPurchaseAmount(p.totalAmount)}</td>
                    <td>
                      <PurchaseStatusBadge status={p.purchaseStatus} />
                    </td>
                    <td>
                      {resolveExpDeliveryDateFromPurchase(p)
                        ? formatPurchaseDate(resolveExpDeliveryDateFromPurchase(p))
                        : '—'}
                    </td>
                    <td>{formatPurchaseDate(p.purchaseDate)}</td>
                    <td className="menu-table-actions-cell">
                      <AdminRowActions
                        name={name}
                        phone={supplier ? supplier.mobile ?? supplier.Mobile : undefined}
                        email={supplier ? supplier.email ?? supplier.Email : undefined}
                        onView={pid != null ? () => openItemsPopup(pid, p) : undefined}
                        onEdit={canEdit && pid != null ? () => openUpdateModal(pid, p) : undefined}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {!loading && filteredPurchases.length > 0 ? (
        <OrdersTablePagination
          currentPage={purchasesPageSafe}
          totalPages={purchasesTotalPages}
          totalItems={sortedPurchases.length}
          pageSize={purchasesPageSize}
          onPageChange={handlePurchasesPageChange}
          onPageSizeChange={handlePurchasesPageSizeChange}
          itemLabel="purchases"
          ariaLabel="Purchases table pagination"
        />
      ) : null}
      </div>

      {itemsPopupPurchaseId != null && (
        <div className="inventory-modal-overlay" role="presentation">
          <div
            className="inventory-modal inventory-purchase-items-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="purchase-items-popup-title"
          >
            <div className="inventory-modal-header">
              <h4 id="purchase-items-popup-title">
                Purchase #{popupHeader.purchaseId ?? itemsPopupPurchaseId}
                {popupHeader
                  ? ` — ${isGroceryOrderPurchase(popupHeader) ? purchaseStoreDisplay(popupHeader) : purchaseSupplierDisplay(popupHeader)}`
                  : ''}
              </h4>
              <button
                type="button"
                className="menu-template-modal-close"
                aria-label="Close"
                onClick={closeItemsPopup}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            {popupLoading ? (
              <p className="inventory-items-hint">Loading purchase items…</p>
            ) : detailError && popupLineItems.length === 0 ? (
              <p className="inventory-status">{detailError}</p>
            ) : popupLineItems.length === 0 ? (
              <p className="inventory-items-hint">No line items for this purchase.</p>
            ) : (
              <div className="menu-table-wrapper inventory-purchase-items-popup-table-wrap">
                <table className="menu-table inventory-purchase-lines-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Quantity</th>
                      <th>Price/unit</th>
                      <th>Discount %</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {popupLineItems.map((line) => (
                      <tr
                        key={
                          line.purchaseItemId ?? `${line.productId}-${line.quantity}`
                        }
                      >
                        <td>
                          {line.productName || productLabel(line.productId)}
                        </td>
                        <td>
                          {line.quantity ?? '—'}
                          {line.unitSymbol ? ` ${line.unitSymbol}` : ''}
                        </td>
                        <td>{line.pricePerUnit ?? '—'}</td>
                        <td>
                          {line.discountPercent != null ? line.discountPercent : '—'}
                        </td>
                        <td>{line.purchaseAmount ?? line.totalPrice ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  {popupTotalAmount != null ? (
                    <tfoot>
                      <tr className="inventory-purchase-total-row">
                        <td colSpan={4} className="inventory-purchase-total-label">
                          <strong>Total</strong>
                        </td>
                        <td className="inventory-purchase-total-amount">{popupTotalAmount}</td>
                      </tr>
                    </tfoot>
                  ) : null}
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {showUpdateModal && updatePurchaseId && (
        <div className="inventory-modal-overlay" role="presentation">
          <div
            className="inventory-modal inventory-purchase-update-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="purchase-update-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="inventory-modal-header inventory-purchase-update-header">
              <h4 id="purchase-update-modal-title">
                Update purchase #{updatePurchaseId}
                {updateSummary ? (
                  <span className="inventory-purchase-update-subtitle">
                    {isGroceryOrderPurchase(updateSummary)
                      ? purchaseStoreDisplay(updateSummary)
                      : purchaseSupplierDisplay(updateSummary)}
                  </span>
                ) : null}
              </h4>
              <button
                type="button"
                className="menu-template-modal-close"
                aria-label="Close"
                onClick={closeUpdateModal}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            <form className="inventory-purchase-update-form" onSubmit={handleUpdateSubmit}>
              <div className="inventory-purchase-update-body">
                {updateLoading ? (
                  <p className="inventory-detail-loading-hint">Loading purchase…</p>
                ) : null}
                {updateError ? (
                  <p className="inventory-purchase-update-error">{updateError}</p>
                ) : null}

                <div className="inventory-form-group">
                  <label htmlFor="purchase-update-status">Purchase status</label>
                  <select
                    id="purchase-update-status"
                    value={updateForm.purchaseStatus}
                    onChange={(e) =>
                      setUpdateForm((prev) => ({
                        ...prev,
                        purchaseStatus: e.target.value,
                      }))
                    }
                    required
                    disabled={updateLoading || updateSubmitting}
                  >
                    {PURCHASE_STATUS_OPTIONS.map((status) => (
                      <option key={status} value={status}>
                        {status}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="inventory-form-group">
                  <label htmlFor="purchase-update-exp-delivery">Expected delivery date</label>
                  <input
                    id="purchase-update-exp-delivery"
                    type="datetime-local"
                    value={updateForm.expDeliveryDate}
                    onChange={(e) =>
                      setUpdateForm((prev) => ({
                        ...prev,
                        expDeliveryDate: e.target.value,
                      }))
                    }
                    disabled={updateLoading || updateSubmitting}
                  />
                </div>
              </div>

              <div className="inventory-purchase-update-footer">
                <button
                  type="submit"
                  className="add-button"
                  disabled={updateLoading || updateSubmitting}
                >
                  {updateSubmitting ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showModal && (
        <div className="inventory-modal-overlay inventory-purchase-modal-overlay">
          <div
            className="inventory-modal inventory-purchase-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="purchase-modal-title"
          >
            <div className="inventory-modal-header">
              <h4 id="purchase-modal-title">Add inventory purchase</h4>
              <button
                type="button"
                className="menu-template-modal-close"
                aria-label="Close"
                onClick={handleClose}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>

            {submitError ? <p className="inventory-status">{submitError}</p> : null}

            <form className="inventory-purchase-form" onSubmit={handleSubmit}>
              <div className="inventory-form-group">
                <label htmlFor="purchase-supplier">Supplier</label>
                <SearchableSelect
                  id="purchase-supplier"
                  value={isStore ? form.supplierName : form.supplierId}
                  onChange={(value) =>
                    setForm((prev) => ({
                      ...prev,
                      ...(isStore ? { supplierName: value } : { supplierId: value }),
                    }))
                  }
                  options={supplierSelectOptions}
                  placeholder="— Select supplier —"
                  searchPlaceholder="Search suppliers"
                  emptyMessage="No suppliers found"
                />
              </div>

              <div className="inventory-form-group">
                <label htmlFor="purchase-date">Purchase date</label>
                <input
                  id="purchase-date"
                  type="date"
                  className="add-menu-form-field"
                  value={form.purchaseDate}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, purchaseDate: e.target.value }))
                  }
                  required
                />
              </div>

              {!isStore ? (
                <>
                  <div className="inventory-form-group">
                    <label htmlFor="purchase-bill-number">Bill number</label>
                    <input
                      id="purchase-bill-number"
                      type="text"
                      className="add-menu-form-field"
                      value={form.billNumber}
                      onChange={(e) =>
                        setForm((prev) => ({ ...prev, billNumber: e.target.value }))
                      }
                      maxLength={50}
                    />
                  </div>
                  <div className="inventory-form-group">
                    <label htmlFor="purchase-payment-type">Payment type</label>
                    <SearchableSelect
                      id="purchase-payment-type"
                      value={form.paymentType}
                      onChange={(paymentType) =>
                        setForm((prev) => ({ ...prev, paymentType }))
                      }
                      options={PURCHASE_PAYMENT_TYPE_OPTIONS}
                      placeholder="— Select payment type —"
                      searchPlaceholder="Search payment types"
                      emptyMessage="No payment types found"
                    />
                  </div>
                </>
              ) : null}

              <div className="inventory-purchase-lines-header">
                <span className="inventory-form-section-label">Products</span>
              </div>

              <div className="inventory-purchase-draft-line">
                <div className="inventory-form-group">
                  <label>Product</label>
                  <SearchableSelect
                    value={form.draftLine.productId}
                    onChange={handleDraftProductChange}
                    options={groceryProductSelectOptions}
                    placeholder="— Select product —"
                    searchPlaceholder="Search products"
                    emptyMessage="No products found"
                  />
                </div>
                <div className="inventory-form-group">
                  <label>Quantity</label>
                  <div className="inventory-input-with-addon">
                    <input
                      type="number"
                      className="add-menu-form-field inventory-input--has-suffix"
                      min="0.01"
                      step="any"
                      value={form.draftLine.quantity}
                      onChange={(e) => handleDraftChange('quantity', e.target.value)}
                    />
                    <span
                      className="inventory-input-addon inventory-input-addon--suffix"
                      aria-hidden="true"
                    >
                      {productUnitSymbol(form.draftLine.productId) || 'unit'}
                    </span>
                  </div>
                </div>
                <div className="inventory-form-group">
                  <label>Price per unit</label>
                  <input
                    type="number"
                    className="add-menu-form-field"
                    min="0"
                    step="0.01"
                    value={form.draftLine.pricePerUnit}
                    onChange={(e) => handleDraftChange('pricePerUnit', e.target.value)}
                  />
                </div>
                <div className="inventory-form-group">
                  <label>Discount %</label>
                  <input
                    type="number"
                    className="add-menu-form-field"
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="Optional"
                    value={form.draftLine.discountPercent}
                    onChange={(e) => handleDraftChange('discountPercent', e.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="add-button inventory-purchase-add-line-btn"
                  onClick={commitDraftLine}
                >
                  Add line
                </button>
              </div>

              <div className="menu-table-wrapper inventory-purchase-committed-table-wrap">
                <table className="menu-table inventory-purchase-lines-table">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>Qty</th>
                      <th>Price/unit</th>
                      <th>Discount %</th>
                      <th>Amount</th>
                      <th aria-label="Actions" />
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="inventory-purchase-empty-table">
                          No products added yet. Fill the fields above and click Add line.
                        </td>
                      </tr>
                    ) : (
                      form.items.map((line, index) => (
                        <tr key={`committed-${index}-${line.productId}`}>
                          <td>{productLabel(line.productId)}</td>
                          <td>
                            {line.quantity}
                            {productUnitSymbol(line.productId) ? (
                              <span className="inventory-unit-symbol">
                                {' '}
                                {productUnitSymbol(line.productId)}
                              </span>
                            ) : null}
                          </td>
                          <td>{line.pricePerUnit}</td>
                          <td>
                            {line.discountPercent !== '' &&
                            line.discountPercent != null &&
                            String(line.discountPercent).trim() !== ''
                              ? line.discountPercent
                              : '—'}
                          </td>
                          <td>{formatLineAmount(line)}</td>
                          <td>
                            <button
                              type="button"
                              className="inventory-purchase-line-remove inventory-purchase-line-remove--table"
                              onClick={() => removeLineItem(index)}
                              aria-label="Remove line"
                            >
                              <FontAwesomeIcon icon={faXmark} aria-hidden />
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              <div className="inventory-form-actions inventory-purchase-form-actions">
                <button type="submit" className="add-button" disabled={isSubmitting}>
                  {isSubmitting ? 'Saving…' : 'Save purchase'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InventoryPurchases;
